/**
 * Media Uploader Utilities - Consolidated
 *
 * Includes file validation, error handling, and checksum calculation utilities
 * for the YAML-config-driven media uploader component.
 */

import { AppLogger } from '@/lib/logger'
import type { Logger } from '@/lib/logger'
import type { UploaderConfig } from '@/lib/mediaUploadTypes'
import { UploadError } from '@/lib/mediaUploadTypes'

// ============================================================================
// Error Messages
// ============================================================================

/**
 * Map of machine-readable error codes to human-readable messages.
 */
export const ERROR_MESSAGES: Record<string, string> = {
  INVALID_FILE_TYPE: 'This file type is not allowed',
  FILE_TOO_LARGE: 'File exceeds the maximum size limit',
  UPLOAD_NOT_VERIFIED: 'Upload could not be verified. Please try again.',
  SIZE_MISMATCH: 'File was corrupted during upload. Please try again.',
  CHECKSUM_MISMATCH: 'File integrity check failed. Please try again.',
  UPLOAD_EXPIRED: 'Upload session expired. Please try again.',
  RATE_LIMITED: 'Too many uploads. Please wait and try again.',
  NETWORK_ERROR: 'Network error during upload. Please check your connection.',
  UNSUPPORTED_PROVIDER: 'Unsupported storage provider returned by server.',
}

// ============================================================================
// Retry Configuration
// ============================================================================

/**
 * Set of error codes that are eligible for automatic retry.
 * Note: UNSUPPORTED_PROVIDER is intentionally NOT included.
 */
export const RETRYABLE_ERRORS: Set<string> = new Set([
  'UPLOAD_NOT_VERIFIED',
  'SIZE_MISMATCH',
  'NETWORK_ERROR',
])

/**
 * Maximum number of retry attempts per file.
 */
export const MAX_RETRIES = 3

// ============================================================================
// File Validation Types
// ============================================================================

/**
 * Result of file validation.
 */
export interface ValidationResult {
  /** Whether the file passed validation */
  valid: boolean
  /** Machine-readable error code if validation failed */
  errorCode?: string
  /** Human-readable error message if validation failed */
  message?: string
}

/**
 * Result of batch file validation.
 */
export interface BatchValidationResult {
  /** Files that passed validation */
  valid: File[]
  /** Files that failed validation with their error details */
  invalid: Array<{
    file: File
    errorCode: string
    message: string
  }>
}

// ============================================================================
// Error Helper Functions
// ============================================================================

/**
 * Check if an error code is eligible for automatic retry.
 *
 * @param errorCode - The machine-readable error code
 * @returns true if the error is retryable, false otherwise
 */
export function isRetryable(errorCode: string): boolean {
  return RETRYABLE_ERRORS.has(errorCode)
}

/**
 * Get the human-readable message for an error code.
 *
 * @param errorCode - The machine-readable error code
 * @returns Human-readable error message, or a generic message if code unknown
 */
export function getErrorMessage(errorCode: string): string {
  return ERROR_MESSAGES[errorCode] ?? 'An unknown error occurred'
}

// ============================================================================
// Single File Validation
// ============================================================================

/**
 * Validate a single file against the uploader configuration.
 *
 * Checks:
 * - MIME type is in allowedTypes list
 * - File size does not exceed maxFileSize
 *
 * @param file - The File object to validate
 * @param config - The uploader configuration
 * @returns Validation result with error details if invalid
 */
export function validateFile(file: File, config: UploaderConfig): ValidationResult {
  // Check MIME type
  if (!config.allowedTypes.includes(file.type)) {
    return {
      valid: false,
      errorCode: 'INVALID_FILE_TYPE',
      message: ERROR_MESSAGES.INVALID_FILE_TYPE,
    }
  }

  // Check file size
  if (file.size > config.maxFileSize) {
    return {
      valid: false,
      errorCode: 'FILE_TOO_LARGE',
      message: ERROR_MESSAGES.FILE_TOO_LARGE,
    }
  }

  return { valid: true }
}

// ============================================================================
// Batch File Validation
// ============================================================================

/**
 * Validate multiple files against the uploader configuration.
 *
 * @param files - Array of File objects to validate
 * @param config - The uploader configuration
 * @returns Object containing arrays of valid and invalid files
 */
export function validateFiles(files: File[], config: UploaderConfig): BatchValidationResult {
  const result: BatchValidationResult = {
    valid: [],
    invalid: [],
  }

  for (const file of files) {
    const validation = validateFile(file, config)

    if (validation.valid) {
      result.valid.push(file)
    } else {
      result.invalid.push({
        file,
        errorCode: validation.errorCode!,
        message: validation.message!,
      })
    }
  }

  return result
}

// ============================================================================
// Checksum Calculation - Constants
// ============================================================================

/** Threshold for switching to chunked hashing (5MB) */
const CHUNK_THRESHOLD = 5 * 1024 * 1024

/** Chunk size for streaming hash calculation (1MB) */
const CHUNK_SIZE = 1024 * 1024

// ============================================================================
// Checksum Calculation - Logger (lazy initialization)
// ============================================================================

let _log: Logger | null = null

function getLog(): Logger {
  if (!_log) {
    _log = AppLogger.getLogger('media-uploader/utils/checksum')
  }
  return _log
}

// ============================================================================
// Checksum Calculation - Helper Functions
// ============================================================================

/**
 * Convert an ArrayBuffer to a lowercase hex string.
 *
 * @param buffer - The ArrayBuffer to convert
 * @returns Lowercase hex string representation
 */
function arrayBufferToHex(buffer: ArrayBuffer): string {
  const hashArray = Array.from(new Uint8Array(buffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

// ============================================================================
// Checksum Calculation - Single-Pass SHA-256
// ============================================================================

/**
 * Calculate SHA-256 hash for a file using single-pass approach.
 * Suitable for files under 5MB to avoid memory pressure.
 *
 * @param file - The File object to hash
 * @returns Promise resolving to lowercase hex hash string
 */
export async function calculateSHA256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
  return arrayBufferToHex(hashBuffer)
}

// ============================================================================
// Checksum Calculation - Chunked SHA-256
// ============================================================================

/**
 * Calculate SHA-256 hash for a file using chunked/streaming approach.
 * Suitable for large files (over 5MB) to avoid loading entire file into memory.
 *
 * Uses incremental hashing by processing the file in 1MB chunks.
 *
 * @param file - The File object to hash
 * @param onProgress - Optional callback for progress updates (0-100)
 * @returns Promise resolving to lowercase hex hash string
 */
export async function calculateSHA256Chunked(
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> {
  const log = getLog()
  const totalSize = file.size
  const totalChunks = Math.ceil(totalSize / CHUNK_SIZE)
  let processedBytes = 0

  // We need to read all chunks and hash them together
  // Web Crypto doesn't have streaming digest, so we collect chunks
  const chunks: Uint8Array[] = []

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    const start = chunkIndex * CHUNK_SIZE
    const end = Math.min(start + CHUNK_SIZE, totalSize)
    const blob = file.slice(start, end)
    const buffer = await blob.arrayBuffer()

    chunks.push(new Uint8Array(buffer))
    processedBytes += buffer.byteLength

    const percent = Math.round((processedBytes / totalSize) * 100)

    log.debug(`Chunk ${chunkIndex + 1}/${totalChunks} completed (${percent}%)`)

    if (onProgress) {
      onProgress(percent)
    }
  }

  // Concatenate all chunks into a single buffer
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const combined = new Uint8Array(totalLength)
  let offset = 0
  for (const chunk of chunks) {
    combined.set(chunk, offset)
    offset += chunk.length
  }

  // Calculate final hash
  const hashBuffer = await crypto.subtle.digest('SHA-256', combined)
  return arrayBufferToHex(hashBuffer)
}

// ============================================================================
// Checksum Calculation - Main Entry Point
// ============================================================================

/**
 * Calculate SHA-256 file checksum using the appropriate strategy.
 *
 * Automatically selects between single-pass (for files under 5MB) and
 * chunked (for files over 5MB) strategies based on file size.
 *
 * @param file - The File object to hash
 * @param onProgress - Optional callback for progress updates (0-100), only called for chunked mode
 * @returns Promise resolving to lowercase hex hash string
 */
export async function calculateFileChecksum(
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> {
  const log = getLog()

  if (file.size > CHUNK_THRESHOLD) {
    log.debug(`Using chunked hashing for file ${file.name} (${file.size} bytes)`)
    return calculateSHA256Chunked(file, onProgress)
  }

  log.debug(`Using single-pass hashing for file ${file.name} (${file.size} bytes)`)
  return calculateSHA256(file)
}

// ============================================================================
// Re-exports
// ============================================================================

export { UploadError }
